import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useRouter } from 'src/routes/hooks';
import { SplashScreen } from 'src/components/loading-screen';
import { RentalCustomer, RentalOrganizationSettings } from 'src/models';
import rentalsCustomerAxios, {
  getRentalsCustomerToken,
  rentalsCustomerEndpoints,
  setRentalsCustomerSession,
} from 'src/utils/rentals-customer-axios';
import { paths } from 'src/routes/paths';

export type RentalsCustomerSession = {
  customer: RentalCustomer;
  organizationSettings: RentalOrganizationSettings | null;
};

type RentalsCustomerAuthContextType = {
  session: RentalsCustomerSession | null;
  loading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: VoidFunction;
};

const RentalsCustomerAuthContext = createContext<RentalsCustomerAuthContextType>({
  session: null,
  loading: true,
  login: async () => {},
  logout: () => {},
});

export function useRentalsCustomerAuth() {
  return useContext(RentalsCustomerAuthContext);
}

export function RentalsCustomerAuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<RentalsCustomerSession | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = getRentalsCustomerToken();
    if (!token) {
      setLoading(false);
      return;
    }
    setRentalsCustomerSession(token);
    rentalsCustomerAxios
      .get(rentalsCustomerEndpoints.current)
      .then((response) => {
        setRentalsCustomerSession(response.data.accessToken);
        setSession(response.data.session);
      })
      .catch(() => {
        setRentalsCustomerSession(null);
        setSession(null);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const login = useCallback(async (username: string, password: string) => {
    const response = await rentalsCustomerAxios.post(rentalsCustomerEndpoints.login, {
      username,
      password,
    });
    setRentalsCustomerSession(response.data.accessToken);
    setSession(response.data.session);
  }, []);

  const logout = useCallback(() => {
    setRentalsCustomerSession(null);
    setSession(null);
  }, []);

  const value = useMemo(
    () => ({ session, loading, login, logout }),
    [session, loading, login, logout]
  );

  return (
    <RentalsCustomerAuthContext.Provider value={value}>
      {children}
    </RentalsCustomerAuthContext.Provider>
  );
}

export function RentalsCustomerGuard({ children }: { children: React.ReactNode }) {
  const { session, loading } = useRentalsCustomerAuth();
  const router = useRouter();

  useEffect(() => {
    if (!loading && !session) {
      router.replace(paths.rentalsPortal.login);
    }
  }, [loading, session, router]);

  if (loading) {
    return <SplashScreen />;
  }
  if (!session) {
    return null;
  }
  return <>{children}</>;
}
